import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { AlertCircle, Trophy, Users } from "lucide-react";
import UnifiedNavHeader from "../../../../components/navigation/UnifiedNavHeader";
import ErrorBoundary from "../../../../components/shared/error/ErrorBoundary";
import { getBrazilianStatus } from "../../../../utils/detectBrazil";
import { footballMobilePtBR } from "../../../../components/mappings/sports-dash-mappings/football-mobile.pt-br";
import { getAllLeagues, getAvailableSeasons } from "../../../../components/dashboards/utils/footballLeagueUtils";
import MobileSportsBar from "../../../../components/homePagePanel/MobileSportsBar";
import {
  useRTDBStandings,
  useRTDBTopScorers,
  cacheWarmer
} from "../rtdb";
import { useQuickPrediction } from "../../../../components/dashboards/hooks/useQuickPrediction";
import MobileFootballPredictionSheet from "../components/mobile/MobileFootballPredictionSheet";
import FootballMobileControlBar from "../components/FootballMobileControlBar";
import MobileFootballGamesView from "../components/mobile/MobileFootballGamesView";
import TabNavigation from "../components/mobile/TabNavigation";
import StandingsTable from "../components/StandingsTable";
import PlayerCard from "../components/PlayerCard";
import { LoadingSpinner, EmptyState } from "../../../../components/shared/loading";
import { Header } from "../../../../components/dashboards/components/shared/Header";
import { FOOTBALL_HEADER_COPY } from "../constants/headerContent";
import QuickPickUpgradeNotice from "../../../../components/QuickPickUpgradeNotice";
import RailNavigation from "../../../../components/dashboards/mobile/RailNavigation";
import { LeagueSheet, MarketSheet, SortSheet } from "../../../../components/dashboards/mobile/FilterSheets";
import { DatePickerSheet } from "../../../../components/dashboards/components/shared/date-picker";
import { ensureISODate } from "../../../../components/dashboards/utils/dateUtils";
import { useFootballFixtures, useFootballDashboardFilters } from "../hooks";

const DEFAULT_FIXTURES_PAGINATION = {
  currentPage: 1,
  perPage: 6,
  totalPages: 1
};

const DEFAULT_PLAYERS_PAGINATION = {
  currentPage: 1,
  perPage: 9,
  totalPages: 1
};

const MOBILE_TABS = [
  { id: 'fixtures', label: 'Live Fixtures' },
  { id: 'standings', label: 'League Tables' },
  { id: 'players', label: 'Top Scorers' }
];

const isFixtureLive = (status) =>
  ["1H", "2H", "HT", "ET", "BT", "P", "SUSP", "INT", "LIVE"].includes(status);

const isFixtureFinal = (status) =>
  ["FT", "AET", "PEN", "AWD", "WO"].includes(status);

const FootballMobileDashboard = () => {
  const [fixturesPagination, setFixturesPagination] = useState(DEFAULT_FIXTURES_PAGINATION);

  // Tab navigation state
  const [activeTab, setActiveTab] = useState('fixtures');

  // Season state for standings and players (league comes from shared filter hook)
  const [standingsSeason, setStandingsSeason] = useState(new Date().getFullYear().toString());
  const [playersSeason, setPlayersSeason] = useState(new Date().getFullYear().toString());
  const [playersPagination, setPlayersPagination] = useState(DEFAULT_PLAYERS_PAGINATION);

  // Prediction sheet state
  const [isPredictionSheetOpen, setIsPredictionSheetOpen] = useState(false);
  const [selectedFixtureForPrediction, setSelectedFixtureForPrediction] = useState(null);
  const [selectedPrediction, setSelectedPrediction] = useState(null);
  const [predictionRequestId, setPredictionRequestId] = useState(0);
  const activePredictionRequestRef = useRef(null);
  const [predictionFlowActive, setPredictionFlowActive] = useState(false);
  const cacheCheckKeyRef = useRef(null);

  const leagues = useMemo(() => getAllLeagues(), []);
  const seasons = useMemo(() => getAvailableSeasons(), []);

  const isBrazilian = getBrazilianStatus();
  const t = useCallback(
    (text) => (isBrazilian ? footballMobilePtBR[text] || text : text),
    [isBrazilian]
  );

  // --- Shared filter state (centralized normalization) ---
  const {
    selectedDate,
    selectedLeague,
    todayDate,
    railFilters,
    railActiveTab,
    railHasActiveFilters,
    railPrimaryTabs,
    railFilterObjects,
    handleRailTabChange,
    handleFilterClick,
    updateRailFilter,
    resetRailFilters,
    showDateSheet,
    setShowDateSheet,
    showLeagueSheet,
    setShowLeagueSheet,
    showSortSheet,
    setShowSortSheet,
    showMarketSheet,
    setShowMarketSheet,
  } = useFootballDashboardFilters();

  // --- Shared fixtures data pipeline (centralized fetch + filter + sort) ---
  const {
    fixtures: filteredFixtures,
    loading: fixturesLoading,
    error: fixturesError,
    refetch: refetchFixtures,
  } = useFootballFixtures({
    date: selectedDate,
    league: selectedLeague,
    market: railFilters?.market || "all",
    sort: railFilters?.sort || "smart",
    enabled: activeTab === "fixtures",
  });

  // Reset pagination when filters change
  useEffect(() => {
    setFixturesPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [selectedDate, selectedLeague]);

  const {
    data: standings = [],
    loading: standingsLoading,
    error: standingsError,
    refetch: refetchStandings
  } = useRTDBStandings({
    league: selectedLeague,
    season: standingsSeason,
    enabled: activeTab === 'standings' && !!selectedLeague
  });

  const {
    data: players = [],
    loading: playersLoading,
    error: playersError,
    refetch: refetchPlayers
  } = useRTDBTopScorers({
    league: selectedLeague,
    season: playersSeason,
    enabled: activeTab === 'players' && !!selectedLeague
  });

  const {
    isLoading: isPredicting,
    selectedGames,
    usageState,
    error: predictionError,
    toggleGameSelection,
    clearSelections,
    predictSelectedGames,
    getGamePrediction,
    isGameSelected,
    isGameFrozen,
    selectionCount,
    checkCachedPredictions,
    refundNotice,
    isButtonDisabled,
    preserveFilterState,
    showUpgradeNotice,
    setShowUpgradeNotice
  } = useQuickPrediction();

  useEffect(() => {
    if (typeof cacheWarmer !== "undefined" && cacheWarmer.warmTodayFixtures) {
      cacheWarmer.warmTodayFixtures().catch(console.error);
    }
  }, []);

  // Prime predictions from cache/Firestore when fixtures stream updates
  useEffect(() => {
    if (activeTab !== 'fixtures') return;
    if (!filteredFixtures?.length) return;
    const gameIds = filteredFixtures.map((f) => f.fixture?.id || f.id).filter(Boolean);
    if (!gameIds.length) return;
    const key = gameIds.slice().sort().join(",");
    if (cacheCheckKeyRef.current === key) return;
    cacheCheckKeyRef.current = key;
    checkCachedPredictions?.("football", gameIds).catch((err) =>
      console.error("checkCachedPredictions failed (Football mobile):", err)
    );
  }, [activeTab, filteredFixtures, checkCachedPredictions]);

  const handleFixturesPageChange = useCallback((page) => {
    setFixturesPagination((prev) => ({
      ...prev,
      currentPage: Math.max(1, Math.min(page, prev.totalPages))
    }));
  }, []);

  const handlePlayersPageChange = useCallback((page) => {
    setPlayersPagination((prev) => ({
      ...prev,
      currentPage: Math.max(1, Math.min(page, prev.totalPages))
    }));
  }, []);

  const handleRefresh = useCallback(() => {
    if (activeTab === 'fixtures') refetchFixtures();
    if (activeTab === 'standings') refetchStandings();
    if (activeTab === 'players') refetchPlayers();
  }, [activeTab, refetchFixtures, refetchStandings, refetchPlayers]);

  // Calculate new selections (excluding frozen games with predictions)
  const newSelectionCount = useMemo(() => {
    return selectedGames.filter(game => !isGameFrozen(game?.fixture?.id)).length;
  }, [selectedGames, isGameFrozen]);

  const handleViewPrediction = useCallback(
    (fixture) => {
      if (!fixture) return;
      const fixturePrediction = getGamePrediction(fixture?.fixture?.id);
      setSelectedFixtureForPrediction(fixture);
      setSelectedPrediction(fixturePrediction);
      setIsPredictionSheetOpen(true);
    },
    [getGamePrediction]
  );

  const handleClosePredictionSheet = useCallback(() => {
    setIsPredictionSheetOpen(false);
    setSelectedFixtureForPrediction(null);
    setSelectedPrediction(null);
  }, []);

  const handlePredictWinners = useCallback(async () => {
    // Track each explicit user request so we only auto-close after a real submission starts/finishes
    setPredictionRequestId((prev) => {
      const next = prev + 1;
      activePredictionRequestRef.current = next;
      return next;
    });
    setPredictionFlowActive(true);
    preserveFilterState?.({
      date: selectedDate,
      country: "",
      league: selectedLeague,
      tab: "fixtures"
    });
    await predictSelectedGames("football");
    setPredictionFlowActive(false);
  }, [predictSelectedGames, preserveFilterState, selectedDate, selectedLeague]);

  // Derived fixture counts for the control bar
  const filteredLiveFixtures = useMemo(
    () => filteredFixtures.filter((fixture) => isFixtureLive(fixture?.fixture?.status?.short)),
    [filteredFixtures]
  );

  const filteredFinishedFixtures = useMemo(
    () => filteredFixtures.filter((fixture) => isFixtureFinal(fixture?.fixture?.status?.short)),
    [filteredFixtures]
  );

  const filteredUpcomingFixtures = useMemo(
    () =>
      filteredFixtures.filter(
        (fixture) =>
          !isFixtureLive(fixture?.fixture?.status?.short) &&
          !isFixtureFinal(fixture?.fixture?.status?.short)
      ),
    [filteredFixtures]
  );

  // Pagination sync
  useEffect(() => {
    setFixturesPagination((prev) => {
      const totalPages = Math.max(1, Math.ceil(filteredFixtures.length / prev.perPage));
      return {
        ...prev,
        totalPages,
        currentPage: Math.min(prev.currentPage, totalPages)
      };
    });
  }, [filteredFixtures]);

  useEffect(() => {
    setPlayersPagination((prev) => {
      const totalPages = Math.max(1, Math.ceil(players.length / prev.perPage));
      return {
        ...prev,
        totalPages,
        currentPage: Math.min(prev.currentPage, totalPages)
      };
    });
  }, [players]);

  useEffect(() => {
    if (isPredictionSheetOpen && selectedFixtureForPrediction) {
      const updatedPrediction = getGamePrediction(selectedFixtureForPrediction?.fixture?.id);
      setSelectedPrediction(updatedPrediction);
    }
  }, [isPredictionSheetOpen, selectedFixtureForPrediction, getGamePrediction]);

  useEffect(() => {
    // Mark that a prediction cycle actually started
    if (isPredicting) {
      return;
    }

    const shouldFinalize =
      !isPredicting &&
      !predictionFlowActive &&
      activePredictionRequestRef.current === predictionRequestId &&
      predictionRequestId > 0;

    if (shouldFinalize) {
      clearSelections();
      handleClosePredictionSheet();
      activePredictionRequestRef.current = null;
    }
  }, [
    clearSelections,
    handleClosePredictionSheet,
    isPredicting,
    predictionFlowActive,
    predictionRequestId
  ]);

  return (
    <ErrorBoundary onReset={handleRefresh}>
      <div className="min-h-screen bg-[#050311] text-white">
        <UnifiedNavHeader title="Football Dashboard" showBackButton />

        {/* Mobile Sports Navigation Bar */}
        <MobileSportsBar />

        <div className="px-4 py-6 pb-24">
          <Header t={t} {...FOOTBALL_HEADER_COPY} />

          <div className="pt-4">
            <RailNavigation
              sport="football"
              primaryTabs={railPrimaryTabs}
              filters={railFilterObjects}
              activeTab={railActiveTab}
              onTabChange={handleRailTabChange}
              onFilterChange={handleFilterClick}
              onReset={resetRailFilters}
              hasActiveFilters={railHasActiveFilters}
              className="border-y border-white/10 bg-[#050311]/95 backdrop-blur"
            />
          </div>

          {/* Tab Navigation */}
          <div className="mt-2">
            <TabNavigation
              tabs={MOBILE_TABS}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </div>

          {activeTab === 'fixtures' && (
            <div className="mt-2 mb-4">
              <div className="border-y border-white/10 bg-[#050311]/95 backdrop-blur px-4 py-3">
                <FootballMobileControlBar
                  t={t}
                  onRefresh={handleRefresh}
                  loading={fixturesLoading}
                  totals={{
                    total: filteredFixtures.length,
                    live: filteredLiveFixtures.length,
                    upcoming: filteredUpcomingFixtures.length,
                    finished: filteredFinishedFixtures.length
                  }}
                />
              </div>
            </div>
          )}

          {/* Fixtures Tab */}
          {activeTab === 'fixtures' && (
            <>
              {fixturesError && (
                <div className="flex items-center gap-3 rounded-2xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-100 mt-4">
                  <AlertCircle className="text-red-300" size={18} />
                  <p>{fixturesError}</p>
                </div>
              )}

              <MobileFootballGamesView
                fixtures={filteredFixtures}
                loading={fixturesLoading}
                error={fixturesError}
                pagination={fixturesPagination}
                onPageChange={handleFixturesPageChange}
                t={t}
                onRetry={handleRefresh}
                isGameSelected={isGameSelected}
                isGameFrozen={isGameFrozen}
                onToggleSelection={toggleGameSelection}
                onViewPrediction={handleViewPrediction}
                getGamePrediction={getGamePrediction}
              />

              <MobileFootballPredictionSheet
                isOpen={isPredictionSheetOpen}
                fixture={selectedFixtureForPrediction}
                prediction={selectedPrediction}
                selectionCount={newSelectionCount}
                usageState={usageState}
                isLoading={isPredicting}
                onPredict={handlePredictWinners}
                onClear={clearSelections}
                onClose={handleClosePredictionSheet}
                t={t}
              />
            </>
          )}

          {/* Standings Tab */}
          {activeTab === 'standings' && (
            <div className="mt-4 space-y-4">
              {/* Season Selector */}
              <div className="border border-white/10 rounded-sm p-4 bg-white/[0.02]">
                <div className="max-w-xs">
                  <label className="block text-xs font-mono uppercase tracking-widest text-white/60 mb-2">
                    Season
                  </label>
                  <select
                    value={standingsSeason}
                    onChange={(e) => setStandingsSeason(e.target.value)}
                    className="w-full px-3 py-2 bg-black border border-white/10 text-white text-sm
                             focus:outline-none focus:border-white/30 transition-colors
                             appearance-none cursor-pointer"
                  >
                    {seasons.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {standingsLoading ? (
                <LoadingSpinner message="Loading standings..." />
              ) : standingsError ? (
                <EmptyState
                  title="Failed to load standings"
                  description={standingsError}
                  action={
                    <button
                      onClick={refetchStandings}
                      className="px-4 py-2 bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30 transition-colors"
                    >
                      Try Again
                    </button>
                  }
                />
              ) : !selectedLeague ? (
                <EmptyState
                  title="No league selected"
                  description="Select a league from the filters above to view standings"
                  icon={Trophy}
                />
              ) : standings.length === 0 ? (
                <EmptyState
                  title="No standings data available"
                  description="Standings data will be available soon"
                  icon={Trophy}
                />
              ) : (
                <div className="border border-white/10 rounded-sm overflow-hidden bg-white/[0.02]">
                  <div className="p-4 border-b border-white/10">
                    <h2 className="text-sm font-mono uppercase tracking-widest text-white flex items-center gap-2">
                      <Trophy className="text-white/60" size={16} />
                      League Standings
                    </h2>
                  </div>
                  <div className="p-4">
                    <StandingsTable standings={standings} mobile={true} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Players Tab */}
          {activeTab === 'players' && (
            <div className="mt-4 space-y-4">
              {/* Season Selector */}
              <div className="border border-white/10 rounded-sm p-4 bg-white/[0.02]">
                <div className="max-w-xs">
                  <label className="block text-xs font-mono uppercase tracking-widest text-white/60 mb-2">
                    Season
                  </label>
                  <select
                    value={playersSeason}
                    onChange={(e) => setPlayersSeason(e.target.value)}
                    className="w-full px-3 py-2 bg-black border border-white/10 text-white text-sm
                             focus:outline-none focus:border-white/30 transition-colors
                             appearance-none cursor-pointer"
                  >
                    {seasons.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {playersLoading ? (
                <LoadingSpinner message="Loading top scorers..." />
              ) : playersError ? (
                <EmptyState
                  title="Failed to load players"
                  description={playersError}
                  action={
                    <button
                      onClick={refetchPlayers}
                      className="px-4 py-2 bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30 transition-colors"
                    >
                      Try Again
                    </button>
                  }
                />
              ) : !selectedLeague ? (
                <EmptyState
                  title="No league selected"
                  description="Select a league from the filters above to view top scorers"
                  icon={Users}
                />
              ) : players.length === 0 ? (
                <EmptyState
                  title="No players data available"
                  description="Top scorers data will be available soon"
                  icon={Users}
                />
              ) : (
                <>
                  <div className="border border-white/10 rounded-sm bg-white/[0.02] p-4">
                    <h2 className="text-sm font-mono uppercase tracking-widest text-white flex items-center gap-2 mb-4">
                      <Users className="text-white/60" size={16} />
                      Top Scorers ({players.length})
                    </h2>
                    <div className="grid grid-cols-1 gap-3">
                      {players
                        .slice(
                          (playersPagination.currentPage - 1) * playersPagination.perPage,
                          playersPagination.currentPage * playersPagination.perPage
                        )
                        .map((player, index) => (
                          <PlayerCard
                            key={player?.player?.id || index}
                            player={player}
                            rank={(playersPagination.currentPage - 1) * playersPagination.perPage + index + 1}
                          />
                        ))}
                    </div>
                  </div>

                  {/* Pagination */}
                  {playersPagination.totalPages > 1 && (
                    <div className="flex items-center justify-center gap-3 px-4">
                      <button
                        onClick={() => handlePlayersPageChange(Math.max(1, playersPagination.currentPage - 1))}
                        disabled={playersPagination.currentPage === 1}
                        className="px-3 py-2 border border-white/10 text-white text-xs font-mono uppercase tracking-widest rounded-sm bg-transparent hover:border-white/30 disabled:opacity-40 transition-all"
                      >
                        Previous
                      </button>
                      <span className="px-4 py-2 text-xs font-mono uppercase tracking-[0.3em] text-gray-400">
                        {playersPagination.currentPage} / {playersPagination.totalPages}
                      </span>
                      <button
                        onClick={() => handlePlayersPageChange(Math.min(playersPagination.totalPages, playersPagination.currentPage + 1))}
                        disabled={playersPagination.currentPage === playersPagination.totalPages}
                        className="px-3 py-2 border border-white/10 text-white text-xs font-mono uppercase tracking-widest rounded-sm bg-transparent hover:border-white/30 disabled:opacity-40 transition-all"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <DatePickerSheet
          isOpen={showDateSheet}
          onClose={() => setShowDateSheet(false)}
          selectedDate={ensureISODate(railFilters.date || selectedDate, todayDate)}
          onDateSelect={(dateValue) => {
            const formatted = ensureISODate(dateValue, todayDate);
            updateRailFilter("date", formatted);
            setShowDateSheet(false);
          }}
        />

        <LeagueSheet
          isOpen={showLeagueSheet}
          onClose={() => setShowLeagueSheet(false)}
          selectedLeagues={String(railFilters.league || 'all')}
          onLeagueSelect={(leagueId) => {
            updateRailFilter("league", leagueId || "all");
            setShowLeagueSheet(false);
          }}
          leagues={leagues}
          sport="football"
        />

        <MarketSheet
          isOpen={showMarketSheet}
          onClose={() => setShowMarketSheet(false)}
          currentMarket={railFilters.market || "all"}
          onMarketChange={(market) => {
            updateRailFilter("market", market);
            setShowMarketSheet(false);
          }}
          sport="football"
        />

        <SortSheet
          isOpen={showSortSheet}
          onClose={() => setShowSortSheet(false)}
          currentSort={railFilters.sort || "smart"}
          onSortChange={(sortValue) => {
            updateRailFilter("sort", sortValue);
            setShowSortSheet(false);
          }}
        />

        {showUpgradeNotice && (
          <QuickPickUpgradeNotice
            onClose={() => setShowUpgradeNotice(false)}
            isMobile={true}
            sport="Soccer"
          />
        )}
      </div>
    </ErrorBoundary>
  );
};

export default FootballMobileDashboard;
